/**
 * Request detail (spec page 3, approved): two panes.
 *   left  - description, stage checklist (Dev sub-stages inline), activity log
 *   right - divisions, requester, also-notify, Impact x Urgency, hours
 *           (estimate, editable actual total, session log),
 *           planner, owner, waiting-on, links, release note
 * Status dropdown in the header. Every field saves on blur/change through
 * updateRequest (concurrency-checked; a conflict shows a message and the
 * live snapshot refreshes the view).
 */
import { useEffect, useState } from 'react';
import {
  PLANNER_SLOTS, STATUSES, TYPES, URGENCY_HINTS, statusIndex, score,
  type DivisionId, type PlannerSlot, type RequestType, type Settings, type Status,
} from '../config';
import {
  NOTIFY_MAX, SESSION_MAX_HOURS, addActivity, ageLabel, clearFields, daysInStage, deleteRequest, deleteSession, editSession,
  logSession, notifyAddr, roundH, safeUrl, setActualTotal, setPlanner, shortId, updateRequest, useActivity,
  usePeople, useSessions,
  type Link, type Me, type Person, type Req, type Session,
} from '../lib/data';
import { DivisionPicker, StatusTag, TierBadge } from '../ui';
import { effectiveSlot } from '../lib/order';
import { Guide } from '../guide';

export function RequestDetail({ me, id, items, settings, onBack }: {
  me: Me; id: string; items: Req[]; settings: Settings; onBack: () => void;
}) {
  const r = items.find((x) => x.id === id);
  const activity = useActivity(id);
  const people = usePeople(me);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);

  if (!r) return <div className="card"><p className="muted">Loading request... (or it was deleted)</p><button className="secondary" onClick={onBack}>Back</button></div>;

  async function save(patch: Partial<Req>) {
    setErr(null);
    try { await updateRequest(me, r!, patch); } catch (e) { setErr((e as Error).message); }
  }
  async function unset(fields: (keyof Req)[]) {
    setErr(null);
    try { await clearFields(me, r!, fields); } catch (e) { setErr((e as Error).message); }
  }
  async function post() {
    if (!note.trim()) return;
    try { await addActivity(me, r!.id, note.trim()); setNote(''); } catch (e) { setErr((e as Error).message); }
  }

  async function remove() {
    setErr(null);
    try { await deleteRequest(r!); onBack(); } catch (e) { setErr((e as Error).message); setConfirmDel(false); }
  }

  const cur = statusIndex(r.status);
  const s = score(r.impact, r.urgency);

  return (
    <div className="detail">
      <div className="detail-head">
        <button className="secondary" onClick={onBack}>Back</button>
        <span className="rid">#{shortId(r.id)}</span>
        <EditText className="title-edit" value={r.title} onSave={(v) => v && save({ title: v })} />
        <span className="spacer" />
        <select className="status-select" value={r.status} onChange={(e) => save({ status: e.target.value as Status })}>
          {STATUSES.map((st) => <option key={st.id} value={st.id}>{st.label}</option>)}
        </select>
        {confirmDel ? (
          <>
            <button className="danger" onClick={remove}>Confirm delete</button>
            <button className="linkish" onClick={() => setConfirmDel(false)}>cancel</button>
          </>
        ) : (
          <button className="secondary danger" onClick={() => setConfirmDel(true)} title="Delete this request, its time sessions and activity">Delete</button>
        )}
      </div>
      {err && <p className="err">{err}</p>}
      <Guide page="detail" />

      <div className="panes">
        <section className="pane-main">
          <div className="card">
            <h2>Description</h2>
            <EditArea value={r.description ?? ''} placeholder="Add the details..." onSave={(v) => v ? save({ description: v }) : unset(['description'])} />
          </div>

          <div className="card">
            <h2>Stages <span className="muted small">in {r.status === 'completed' ? 'Completed' : 'this stage'} {daysInStage(r)}d</span></h2>
            <ol className="stages">
              {STATUSES.map((st, i) => (
                <li key={st.id} className={i < cur ? 'done' : i === cur ? 'current' : ''}>
                  <button className="linkish" onClick={() => save({ status: st.id })} title="Move here">
                    <span className="box">{i < cur ? 'x' : i === cur ? '>' : ''}</span>{st.label}
                  </button>
                  {r.stageTimes?.[st.id] && <span className="muted small"> {r.stageTimes[st.id].toDate().toLocaleDateString()}</span>}
                </li>
              ))}
            </ol>
          </div>

          <div className="card">
            <h2>Activity</h2>
            <ul className="activity">
              {activity.map((a) => (
                <li key={a.id} className={`act-${a.kind}`}>
                  <span className="when">{a.createdAt ? a.createdAt.toDate().toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'now'}</span>
                  <b>{a.author}:</b> {a.text}
                </li>
              ))}
              {!activity.length && <li className="muted">No activity yet.</li>}
            </ul>
            <div className="capture">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note..." onKeyDown={(e) => e.key === 'Enter' && post()} />
              <button onClick={post} disabled={!note.trim()}>Add note</button>
            </div>
          </div>
        </section>

        <aside className="pane-side card">
          <Field label="Status"><StatusTag status={r.status} /></Field>
          <Field label="Type">
            <select value={r.type} onChange={(e) => save({ type: e.target.value as RequestType })}>
              {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Divisions">
            <DivisionPicker value={r.divisions} onChange={(v: DivisionId[]) => v.length && save({ divisions: v })} />
          </Field>
          <Field label="Asked by">
            <EditText value={r.requesterName} onSave={(v) => v && save({ requesterName: v })} />
            <div className="muted small">{r.requesterEmail || 'no email'} &middot; {ageLabel(r.createdAt)} ago &middot; via {r.source}{r.addedBy ? ` (${r.addedBy})` : ''}</div>
            {r.urgencyHint && <div className="muted small">Their urgency: {URGENCY_HINTS.find((u) => u.id === r.urgencyHint)?.label}</div>}
          </Field>
          <Field label="Also notify">
            <Notify emails={r.notify ?? []} people={people} exclude={r.requesterEmail}
              onSave={(l) => l.length ? save({ notify: l }) : unset(['notify'])} />
          </Field>
          <Field label="Priority">
            <div className="score-row">
              <label>Impact <ScoreSelect value={r.impact} onChange={(v) => v ? save({ impact: v }) : unset(['impact'])} /></label>
              <span>x</span>
              <label>Urgency <ScoreSelect value={r.urgency} onChange={(v) => v ? save({ urgency: v }) : unset(['urgency'])} /></label>
              <span>=</span>
              <TierBadge r={r} settings={settings} />
            </div>
            {s == null && <div className="muted small">Set both to score it.</div>}
            {r.rankManual && <div className="muted small">Moved by hand in the Lineup.</div>}
          </Field>
          <Field label="Hours">
            <Hours me={me} r={r} onErr={setErr}
              onEst={(v) => v == null ? unset(['estHours']) : save({ estHours: v })} />
          </Field>
          <Field label="Planner">
            <select value={effectiveSlot(r.plannerSlot, r.plannerDate).slot} onChange={(e) => setPlanner(me, r, e.target.value as PlannerSlot).catch((x) => setErr((x as Error).message))}>
              {PLANNER_SLOTS.map((p) => <option key={p} value={p}>{p === 'none' ? 'Not planned' : p === 'week' ? 'This week' : p[0].toUpperCase() + p.slice(1)}</option>)}
            </select>
          </Field>
          <Field label="Owner"><EditText value={r.owner ?? ''} placeholder="Baker" onSave={(v) => v ? save({ owner: v }) : unset(['owner'])} /></Field>
          <Field label="Waiting on">
            <EditText value={r.waitingOn ?? ''} placeholder="Nobody - not blocked" onSave={(v) => v ? save({ waitingOn: v }) : unset(['waitingOn'])} />
          </Field>
          <Field label="Links"><Links links={r.links ?? []} onSave={(l) => l.length ? save({ links: l }) : unset(['links'])} /></Field>
          {cur >= statusIndex('release') && (
            <Field label="Release note">
              {r.status === 'completed' && !r.releaseNote?.trim() && (r.requesterEmail || r.notify?.length) && (
                <div className="hint">The "Done" email (requester + Also notify) goes out as soon as you add this note.</div>
              )}
              <EditArea value={r.releaseNote ?? ''} placeholder="What shipped, in plain words - goes to the requester and the Completed feed" onSave={(v) => v ? save({ releaseNote: v }) : unset(['releaseNote'])} />
            </Field>
          )}
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="field"><div className="field-label">{label}</div>{children}</div>;
}

/** Text input that saves on blur / Enter when the value actually changed. */
function EditText({ value, onSave, placeholder, className }: { value: string; onSave: (v: string) => void; placeholder?: string; className?: string }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const commit = () => { if (v.trim() !== value) onSave(v.trim()); };
  return <input className={className} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />;
}

function EditArea({ value, onSave, placeholder }: { value: string; onSave: (v: string) => void; placeholder?: string }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return <textarea rows={5} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} onBlur={() => v.trim() !== value && onSave(v.trim())} />;
}

function ScoreSelect({ value, onChange }: { value?: number; onChange: (v: number | null) => void }) {
  return (
    <select value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}>
      <option value="">-</option>
      {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

/**
 * Estimated vs actual. "Log session" adds a session and moves the total in the
 * same write; the total itself is editable, and any gap between it and the
 * session sum shows as a manual adjustment.
 */
function Hours({ me, r, onErr, onEst }: {
  me: Me; r: Req; onErr: (m: string | null) => void; onEst: (v: number | null) => void;
}) {
  const sessions = useSessions(r.id);
  const [h, setH] = useState('');
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const actual = r.loggedHours ?? 0;
  const sum = roundH(sessions.reduce((t, x) => t + x.hours, 0));
  const adjust = roundH(actual - sum);
  const pct = r.estHours ? actual / r.estHours : 0;
  const run = async (f: () => Promise<void>) => { onErr(null); try { await f(); } catch (e) { onErr((e as Error).message); } };
  const log = () => {
    const n = Number(h);
    if (!h.trim() || Number.isNaN(n) || n <= 0 || n > SESSION_MAX_HOURS) { onErr(`Log between 0 and ${SESSION_MAX_HOURS} hours.`); return; }
    void run(async () => { await logSession(me, r, n, { note }); setH(''); setNote(''); });
  };
  return (
    <div className="hours">
      <div className="score-row">
        <label>Est <NumInput value={r.estHours} onSave={onEst} /></label>
        <label>Actual <NumInput value={r.loggedHours} onSave={(v) => run(() => setActualTotal(me, r, v))} /></label>
        {r.estHours ? <span className={`small ${pct > 1 ? 'danger-text' : 'muted'}`}>{Math.round(pct * 100)}% of est</span> : null}
      </div>
      {r.estHours ? <div className="capbar hours-bar"><span style={{ width: `${Math.min(100, pct * 100)}%` }} className={pct > 1 ? 'over-cap' : ''} /></div> : null}
      <div className="session-add">
        <input className="num-input" inputMode="decimal" placeholder="h" value={h} aria-label="Session hours"
          onChange={(e) => setH(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && log()} />
        <input placeholder="What was done (optional)" value={note} maxLength={500} aria-label="Session note"
          onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && log()} />
        <button className="secondary" onClick={log} disabled={!h.trim()}>Log session</button>
      </div>
      {sessions.length > 0 && (
        <div className="sessions">
          {sessions.map((x) => editing === x.id
            ? <SessionEdit key={x.id} s={x} onCancel={() => setEditing(null)}
                onSave={(p) => run(async () => { await editSession(me, r, x, p); setEditing(null); })} />
            : (
              <div key={x.id} className="session-row">
                <span className="muted">{x.date ? x.date.toDate().toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }) : 'now'}</span>
                <span>{x.by}</span>
                <b>{x.hours}h</b>
                <span className="session-note" title={x.note}>{x.note ?? ''}</span>
                <button className="linkish" onClick={() => setEditing(x.id)}>edit</button>
                <button className="linkish danger-text" onClick={() => run(() => deleteSession(me, r, x))}>remove</button>
              </div>
            ))}
        </div>
      )}
      {(sessions.length > 0 || actual > 0) && (
        <div className="muted small">
          {sessions.length} session{sessions.length === 1 ? '' : 's'} = {sum}h
          {adjust !== 0 && <> &middot; manual adjustment {adjust > 0 ? '+' : ''}{adjust}h</>}
        </div>
      )}
    </div>
  );
}

function SessionEdit({ s, onSave, onCancel }: {
  s: Session; onSave: (p: { hours: number; note: string; date: Date }) => void; onCancel: () => void;
}) {
  const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [h, setH] = useState(String(s.hours));
  const [note, setNote] = useState(s.note ?? '');
  const [date, setDate] = useState(ymd(s.date ? s.date.toDate() : new Date()));
  const n = Number(h);
  const ok = h.trim() !== '' && !Number.isNaN(n) && n > 0 && n <= SESSION_MAX_HOURS && /^\d{4}-\d{2}-\d{2}$/.test(date);
  return (
    <div className="session-row editing">
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Session date" />
      <input className="num-input" inputMode="decimal" value={h} onChange={(e) => setH(e.target.value)} aria-label="Session hours" />
      <input value={note} maxLength={500} placeholder="Note" onChange={(e) => setNote(e.target.value)} aria-label="Session note" />
      <button className="secondary" disabled={!ok} onClick={() => onSave({ hours: n, note, date: new Date(`${date}T12:00:00`) })}>Save</button>
      <button className="linkish" onClick={onCancel}>cancel</button>
    </div>
  );
}

function NumInput({ value, onSave }: { value?: number; onSave: (v: number | null) => void }) {
  const [v, setV] = useState(value?.toString() ?? '');
  useEffect(() => setV(value?.toString() ?? ''), [value]);
  const commit = () => {
    const n = v.trim() === '' ? null : Number(v);
    if (n != null && (Number.isNaN(n) || n < 0)) { setV(value?.toString() ?? ''); return; }
    if (n !== (value ?? null)) onSave(n);
  };
  return <input className="num-input" inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} />;
}

/**
 * Extra people CC'd on this request's "Got it" / "Done" emails. Trust Roofing
 * addresses only; the people list feeds autocomplete (you can type a name).
 */
function Notify({ emails, people, exclude, onSave }: {
  emails: string[]; people: Person[]; exclude?: string; onSave: (l: string[]) => void;
}) {
  const [v, setV] = useState('');
  const [bad, setBad] = useState(false);
  const nameOf = (e: string) => people.find((p) => p.email.toLowerCase() === e)?.name;
  const add = () => {
    // accept "Name <email>" / a picked name / a raw address
    const typed = v.trim();
    const byName = people.find((p) => p.name.toLowerCase() === typed.toLowerCase());
    const addr = notifyAddr(byName?.email ?? typed.replace(/^.*<(.+)>$/, '$1'));
    if (!addr) { setBad(true); return; }
    if (!emails.includes(addr) && addr !== exclude?.trim().toLowerCase()) onSave([...emails, addr]);
    setV(''); setBad(false);
  };
  const full = emails.length >= NOTIFY_MAX;
  return (
    <div>
      {emails.map((e) => (
        <div key={e} className="link-row">
          <span title={e}>{nameOf(e) ?? e}</span>
          <button className="linkish danger-text" onClick={() => onSave(emails.filter((x) => x !== e))}>remove</button>
        </div>
      ))}
      {!full && (
        <div className="link-add">
          <input list="notify-people" value={v} placeholder="Name or @trustroofing.com email"
            onChange={(e) => { setV(e.target.value); setBad(false); }} onKeyDown={(e) => e.key === 'Enter' && add()} />
          <datalist id="notify-people">
            {people.filter((p) => p.email && !emails.includes(p.email.toLowerCase())).map((p) => <option key={p.uid} value={p.email}>{p.name}</option>)}
          </datalist>
          <button className="secondary" onClick={add} disabled={!v.trim()}>Add</button>
        </div>
      )}
      {bad && <div className="err small">Trust Roofing emails only (name@trustroofing.com).</div>}
      <div className="muted small">CC'd on the "Got it" and "Done" emails.</div>
    </div>
  );
}

function Links({ links, onSave }: { links: Link[]; onSave: (l: Link[]) => void }) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const add = () => {
    if (!safeUrl(url)) return;
    onSave([...links, { label: label.trim() || url.trim(), url: url.trim() }]);
    setUrl(''); setLabel('');
  };
  return (
    <div>
      {links.map((l, i) => (
        <div key={i} className="link-row">
          {safeUrl(l.url) ? <a href={l.url} target="_blank" rel="noreferrer noopener">{l.label}</a> : <span title="Not a web link - not clickable">{l.label}: {l.url}</span>}
          <button className="linkish danger-text" onClick={() => onSave(links.filter((_, j) => j !== i))}>remove</button>
        </div>
      ))}
      <div className="link-add">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (email thread, HubSpot...)" />
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." onKeyDown={(e) => e.key === 'Enter' && add()} />
        <button className="secondary" onClick={add} disabled={!safeUrl(url)}>Add</button>
      </div>
    </div>
  );
}
